import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { environment } from '../../../../../environments/environment';
import { AuthService } from '../../../../@core/auth/auth.service';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { ErrorService } from '../../../../@core/utils/error.service';
import { ActionTypeForInvetory } from '../../../../shared/enum';
import { Utils } from '../../../../shared/utility';
import { HandlingUnitBulkDelete, HandlingUnitBulkMove } from '../../inventory/models/handling-unit-bulk-actions';
import { HandlingUnitDetailDTO } from '../../inventory/models/handling-unit-detail';
import { HandlingUnitMasterDTO } from '../../inventory/models/handling-unit-master';
import { InventoryLotDTO, InventoryLotSummaryDTO } from '../../inventory/models/inventory-lot';
import { InventoryPackageTypeDTO } from '../../inventory/models/inventory-package-type';
import { InventoryStatusDTO } from '../../inventory/models/inventory-status';
import { InventoryItemDTO } from '../../inventory/models/item';
import { InventoryItemAssemblyAttributeDTO, InventoryItemAssemblyDTO } from '../../inventory/models/item-assembly';
import { InventoryItemAttributeDTO } from '../../inventory/models/item-attribute';
import { InventoryLotAttributeDTO } from '../../inventory/models/lot-attribute';
import { ReasonCode } from '../../inventory/models/reason-code';
import { WorkOrder } from '../../work-orders/models/work-order';




@Injectable({
  providedIn: "root",
})
export class InventoryService {
  private destroyRef = inject(DestroyRef);
  protected basePath = "/";

  constructor(
    protected httpClient: HttpClient,
    private authService: AuthService,
    private errorService: ErrorService
  ) {
    this.basePath = environment.PLATFORM_API_URL;
    this.refreshLocalLots();
  }

  public allLots: InventoryLotDTO[] = [];
  public allLotsForFilters: InventoryLotDTO[] = [];

  public cachedLotSummaries: InventoryLotSummaryDTO[] = [];

  public getInventoryItems(includeInactive: boolean = false) {
    let queryParameters = new HttpParams();

    if (includeInactive !== undefined && includeInactive !== null) {
      queryParameters = queryParameters.set(
        "IncludeInactive",
        <any>includeInactive
      );
    }

    return this.httpClient.get<InventoryItemDTO[]>(
      `${this.basePath}/api/inventory/items`,
      {
        params: queryParameters
      }
    );
  }

  public getInventoryStatuses(includeInactive: boolean = false) {
    let queryParameters = new HttpParams();

    if (includeInactive !== undefined && includeInactive !== null) {
      queryParameters = queryParameters.set(
        "IncludeInactive",
        <any>includeInactive
      );
    }

    return this.httpClient.get<InventoryStatusDTO[]>(
      `${this.basePath}/api/inventory/HandlingUnitMaster/status`,
      {
        params: queryParameters
      }
    );
  }

  public getInventoryItem(itemId: number): Observable<InventoryItemDTO> {
    return this.httpClient.get<InventoryItemDTO>(
      `${this.basePath}/api/inventory/items/${itemId}`);
  }

  public postInventoryItem(
    item: InventoryItemDTO,
    saveERPFlag: boolean = false
  ): Observable<InventoryItemDTO> {
    return this.httpClient.post<InventoryItemDTO>(
      `${this.basePath}/api/inventory/items?saveERPFlag=${saveERPFlag}`,
      item
    );
  }

  public putInventoryItem(
    item: InventoryItemDTO,
    saveERPFlag: boolean = false
  ): Observable<InventoryItemDTO> {
    return this.httpClient.put<InventoryItemDTO>(
      `${this.basePath}/api/inventory/items/${item.id}?saveERPFlag=${saveERPFlag}`,
      item
    );
  }

  public deleteInventoryItem(
    id: number,
    saveERPFlag: boolean = false
  ): Observable<any> {
    return this.httpClient.delete(
      `${this.basePath}/api/inventory/items/${id}?saveERPFlag=${saveERPFlag}`
    );
  }

  public getInventorySummary(includeInactive: boolean = false) {
    let queryParameters = new HttpParams();

    if (includeInactive !== undefined && includeInactive !== null) {
      queryParameters = queryParameters.set(
        "IncludeInactive",
        <any>includeInactive
      );
    }

    queryParameters = queryParameters.set("inventorySummary", <any>true);

    return this.httpClient.get<HandlingUnitMasterDTO[]>(
      `${this.basePath}/api/inventory/HandlingUnitMaster`,
      {
        params: queryParameters
      }
    );
  }

  //Hum Calls

  public getWorkOrdersByLot(lotId: number): Observable<WorkOrder[]> {
    return this.httpClient.get<WorkOrder[]>(
      `${this.basePath}/api/inventory/HandlingUnitMaster/workorders/${lotId}`);
  }

  public getInventoryHUMs(
    includeInactive: boolean = false,
    lotId: number = 0,
    orderNumber?: string,
    warehouseId = 0,
    roomId = 0,
    roomLocationId = 0,
    includeTransactions = false,
    includeWorkOrders = false,
    inventorySummary: boolean = false,
    includeOnOrder = false,
    isOrderData = false
  ) {
    let queryParameters = new HttpParams();

    if (includeInactive !== undefined && includeInactive !== null) {
      queryParameters = queryParameters.set(
        "IncludeInactive",
        <any>includeInactive
      );
    }

    if (includeTransactions !== undefined && includeTransactions !== null) {
      queryParameters = queryParameters.set(
        "includeTransactions",
        <any>includeTransactions
      );
    }

    if (includeWorkOrders !== undefined && includeWorkOrders !== null) {
      queryParameters = queryParameters.set(
        "includeWorkOrders",
        <any>includeWorkOrders
      );
    }

    if (lotId && lotId !== 0) {
      queryParameters = queryParameters.set("lotId", <any>lotId);
    }

    if (warehouseId && warehouseId !== 0) {
      queryParameters = queryParameters.set("warehouseId", <any>warehouseId);
    }

    if (roomId && roomId !== 0) {
      queryParameters = queryParameters.set("roomId", <any>roomId);
    }

    if (roomLocationId && roomLocationId !== 0) {
      queryParameters = queryParameters.set("locationId", <any>roomLocationId);
    }

    if (orderNumber && orderNumber !== "") {
      queryParameters = queryParameters.set("orderNumber", <any>orderNumber);
    }

    if (includeOnOrder) {
      queryParameters = queryParameters.set(
        "includeOnOrder",
        <any>includeOnOrder
      );
    }

    if (isOrderData) {
      queryParameters = queryParameters.set(
        "isOrderData",
        <any>isOrderData
      );
    }

    return this.httpClient.get<HandlingUnitMasterDTO[]>(
      `${this.basePath}/api/inventory/HandlingUnitMaster`,
      {
        params: queryParameters
      }
    );
  }

  public getInventoryHUM(
    id: string,
    includeInactive: boolean = false
  ): Observable<HandlingUnitMasterDTO> {
    let queryParameters = new HttpParams();
    queryParameters = queryParameters.set(
      "IncludeInactive",
      <any>includeInactive
    );

    // to determine the Content-Type header
    return this.httpClient.get<HandlingUnitMasterDTO>(
      `${this.basePath}/api/inventory/HandlingUnitMaster/${id}`,
      {
        params: queryParameters
      }
    );
  }

  public getInventoryHUMByBarcode(
    id: string,
    includeInactive: boolean = false
  ): Observable<HandlingUnitMasterDTO> {
    let queryParameters = new HttpParams();
    queryParameters = queryParameters.set(
      "IncludeInactive",
      <any>includeInactive
    );

    return this.httpClient.get<HandlingUnitMasterDTO>(
      `${this.basePath}/api/inventory/HandlingUnitMaster/barcode/${id}`,
      {
        params: queryParameters
      }
    );
  }

  public reprintInventoryHUM(id: string, printerId: string) {

    let queryParameters = new HttpParams();

    queryParameters = queryParameters.set("id", id);
    queryParameters = queryParameters.set("printerId", printerId);

    return this.httpClient.put<HandlingUnitMasterDTO>(
      `${this.basePath}/api/inventory/HandlingUnitMaster/reprint`,
      null,
      {
        params: queryParameters,
      }
    );
  }

  public bulkReprintInventoryHUM(ids: number[], printerId: number) {
    let queryParameters = new HttpParams();

    queryParameters = queryParameters.set("printerId", printerId.toString());

    return this.httpClient.put<HandlingUnitMasterDTO>(
      `${this.basePath}/api/inventory/HandlingUnitMaster/reprintBulk`,
      ids,
      {
        params: queryParameters,
      }
    );
  }

  public reprintBulkTag(
    barcodeList: string[],
    printerId: number,
    printQuantity: number,
    tagName: string
  ) {

    let queryParameters = new HttpParams();

    queryParameters = queryParameters.set("printerId", printerId.toString());
    queryParameters = queryParameters.set(
      "printQuantity",
      printQuantity.toString()
    );
    queryParameters = queryParameters.set("tagName", tagName);

    

    return this.httpClient.put<HandlingUnitMasterDTO>(
      `${this.basePath}/api/inventory/HandlingUnitMaster/reprintBulkTag`,
      barcodeList,
      {
        params: queryParameters,
      }
    );
  }

  public postInventoryHUM(
    item: HandlingUnitMasterDTO,
    saveERPFlag: boolean = false
  ): Observable<HandlingUnitMasterDTO> {

    return this.httpClient.post<HandlingUnitMasterDTO>(
      `${this.basePath}/api/inventory/HandlingUnitMaster?saveERPFlag=${saveERPFlag}`,
      item);
  }

  public postBulkInventoryHUM(
    items: HandlingUnitMasterDTO[],
    printerId: number = 0,
    printQuantity: number = 1,
    saveERPFlag: boolean = false
  ): Observable<HandlingUnitMasterDTO[]> {

    let queryParameters = new HttpParams();

    if (printerId !== undefined && printerId !== null && printerId != 0) {
      queryParameters = queryParameters.set("printerId", <any>printerId);
    }
    queryParameters = queryParameters.set("printQuantity", <any>printQuantity);

    return this.httpClient.post<HandlingUnitMasterDTO[]>(
      `${this.basePath}/api/inventory/HandlingUnitMaster/bulk?saveERPFlag=${saveERPFlag}`,
      items,

      {
        params: queryParameters
      }
    );
  }

  public putInventoryHUM(
    item: HandlingUnitMasterDTO,
    isERPSave?: boolean,
    actionType?: ActionTypeForInvetory
  ): Observable<HandlingUnitMasterDTO> {

    let queryParameters = new HttpParams();

    if (isERPSave)
      queryParameters = queryParameters.set(
        "saveERPFlag",
        isERPSave.toString()
      );

    if (!Utils.IsStringNullOrEmpty(actionType)) {
      queryParameters = queryParameters.set(
        "actionType",
        actionType.toString()
      );
    }

    return this.httpClient.put<HandlingUnitMasterDTO>(
      `${this.basePath}/api/inventory/HandlingUnitMaster/${item.id}`,
      item,
      {
        params: queryParameters
      }
    );
  }

  public deleteInventoryHUM(id: number): Observable<any> {
    return this.httpClient.delete(
      `${this.basePath}/api/inventory/HandlingUnitMaster/${id}`);
  }

  public getLots(includeInactive: boolean = false, workOrderId: number = 0) {
    let queryParameters = new HttpParams();

    if (includeInactive !== undefined && includeInactive !== null) {
      queryParameters = queryParameters.set(
        "IncludeInactive",
        <any>includeInactive
      );
    }

    queryParameters = queryParameters.set(
      "workOrderId",
      workOrderId.toString()
    );

    return this.httpClient.get<InventoryLotDTO[]>(
      `${this.basePath}/api/inventory/lots`,
      {
        params: queryParameters
      }
    );
  }

  public getLotsForFilters(includeInactive: boolean = false) {
    let queryParameters = new HttpParams();

    if (includeInactive !== undefined && includeInactive !== null) {
      queryParameters = queryParameters.set(
        "IncludeInactive",
        <any>includeInactive
      );
    }

    return this.httpClient.get<InventoryLotDTO[]>(
      `${this.basePath}/api/inventory/lots/forFilters`,
      {
        params: queryParameters
      }
    );
  }

  //Lot Calls

  public getInventoryLots(includeInactive: boolean = false) {
    let queryParameters = new HttpParams();

    if (includeInactive !== undefined && includeInactive !== null) {
      queryParameters = queryParameters.set(
        "IncludeInactive",
        <any>includeInactive
      );
    }

    return this.httpClient.get<InventoryLotDTO[]>(
      `${this.basePath}/api/inventory/lots`,
      {
        params: queryParameters
      }
    );
  }

  public getInventoryLotsForFilters(includeInactive: boolean = false) {
    let queryParameters = new HttpParams();

    if (includeInactive !== undefined && includeInactive !== null) {
      queryParameters = queryParameters.set(
        "IncludeInactive",
        <any>includeInactive
      );
    }

    return this.httpClient.get<InventoryLotDTO[]>(
      `${this.basePath}/api/inventory/lots/forFilters`,
      {
        params: queryParameters
      }
    );
  }

  public getInventoryLot(lotId: number): Observable<InventoryLotDTO> {
    return this.httpClient.get<InventoryLotDTO>(
      `${this.basePath}/api/inventory/lots/${lotId}`);
  }

  public postInventoryLot(lot: InventoryLotDTO): Observable<InventoryLotDTO> {
    return this.httpClient.post<InventoryLotDTO>(
      `${this.basePath}/api/inventory/lots`,
      lot);
  }

  public putInventoryLot(lot: InventoryLotDTO): Observable<InventoryLotDTO> {
    return this.httpClient.put<InventoryLotDTO>(
      `${this.basePath}/api/inventory/lots/${lot.id}`,
      lot);
  }

  public bulkUpdateProductCodeByLot(lotId: number, inventoryItemId: number): Observable<any> {
    return this.httpClient.put<any>(
      `${this.basePath}/api/inventory/HandlingUnitDetail/BulkUpdateByLot?lotId=${lotId}&inventoryItemId=${inventoryItemId}`,
      null);
  }

  public deleteInventoryLot(id: number): Observable<any> {
    return this.httpClient.delete(`${this.basePath}/api/inventory/lots/${id}`);
  }

  //item attributes

  public getInventoryItemAttributes(includeInactive: boolean = false) {
    let queryParameters = new HttpParams();

    if (includeInactive !== undefined && includeInactive !== null) {
      queryParameters = queryParameters.set(
        "IncludeInactive",
        <any>includeInactive
      );
    }

    return this.httpClient.get<InventoryItemAttributeDTO[]>(
      `${this.basePath}/api/inventory/itemAttributes`,
      {
        params: queryParameters
      }
    );
  }

  public getInventoryItemAttribute(
    itemAttributesId: number
  ): Observable<InventoryItemAttributeDTO> {

    return this.httpClient.get<InventoryItemAttributeDTO>(
      `${this.basePath}/api/inventory/itemAttributes/${itemAttributesId}`);
  }

  public postInventoryItemAttributes(
    itemAttributes: InventoryItemAttributeDTO
  ): Observable<InventoryItemAttributeDTO> {

    return this.httpClient.post<InventoryItemAttributeDTO>(
      `${this.basePath}/api/inventory/itemAttributes`,
      itemAttributes);
  }

  public putInventoryItemAttributes(
    itemAttributes: InventoryItemAttributeDTO
  ): Observable<InventoryItemAttributeDTO> {

    return this.httpClient.put<InventoryItemAttributeDTO>(
      `${this.basePath}/api/inventory/itemAttributes/${itemAttributes.id}`,
      itemAttributes);
  }

  public deleteInventoryItemAttributes(id: number): Observable<any> {
    return this.httpClient.delete(
      `${this.basePath}/api/inventory/itemAttributes/${id}`);
  }

  //lot attributes

  public getInventoryLotAttributes(includeInactive: boolean = false) {
    let queryParameters = new HttpParams();

    if (includeInactive !== undefined && includeInactive !== null) {
      queryParameters = queryParameters.set(
        "IncludeInactive",
        <any>includeInactive
      );
    }

    return this.httpClient.get<InventoryLotAttributeDTO[]>(
      `${this.basePath}/api/inventory/lotAttributes`,
      {
        params: queryParameters
      }
    );
  }

  public getInventoryLotAttribute(
    lotAttributesId: number
  ): Observable<InventoryLotAttributeDTO> {

    return this.httpClient.get<InventoryLotAttributeDTO>(
      `${this.basePath}/api/inventory/lotAttributes/${lotAttributesId}`
    );
  }

  public postInventoryLotAttributes(
    lotAttributes: InventoryLotAttributeDTO
  ): Observable<InventoryLotAttributeDTO> {
    return this.httpClient.post<InventoryLotAttributeDTO>(
      `${this.basePath}/api/inventory/lotAttributes`,
      lotAttributes
    );
  }

  public putInventoryLotAttributes(
    lotAttributes: InventoryLotAttributeDTO
  ): Observable<InventoryLotAttributeDTO> {

    return this.httpClient.put<InventoryLotAttributeDTO>(
      `${this.basePath}/api/inventory/lotAttributes/${lotAttributes.id}`,
      lotAttributes);
  }

  public deleteInventoryLotAttributes(id: number): Observable<any> {
    return this.httpClient.delete(
      `${this.basePath}/api/inventory/lotAttributes/${id}`);
  }

  //Inventory Item Assemblies -------------

  public getInventoryItemAssemblies(includeInactive: boolean = false) {
    let queryParameters = new HttpParams();

    if (includeInactive !== undefined && includeInactive !== null) {
      queryParameters = queryParameters.set(
        "IncludeInactive",
        <any>includeInactive
      );
    }

    return this.httpClient.get<InventoryItemAssemblyDTO[]>(
      `${this.basePath}/api/inventory/ItemAssemblies`,
      {
        params: queryParameters
      }
    );
  }

  public getInventoryItemAssembly(
    id: number
  ): Observable<InventoryItemAssemblyDTO> {

    return this.httpClient.get<InventoryItemAssemblyDTO>(
      `${this.basePath}/api/inventory/ItemAssemblies/${id}`
    );
  }

  public getInventoryItemAssemblyByItemId(
    itemId: number
  ): Observable<InventoryItemAssemblyDTO> {

    return this.httpClient.get<InventoryItemAssemblyDTO>(
      `${this.basePath}/api/inventory/ItemAssemblies/byItem/${itemId}`);
  }

  public postInventoryItemAssembly(
    itemAssembly: InventoryItemAssemblyDTO
  ): Observable<InventoryItemAssemblyDTO> {
    return this.httpClient.post<InventoryItemAssemblyDTO>(
      `${this.basePath}/api/inventory/ItemAssemblies`,
      itemAssembly);
  }

  public putInventoryItemAssembly(
    itemAssembly: InventoryItemAssemblyDTO
  ): Observable<InventoryItemAssemblyDTO> {

    return this.httpClient.put<InventoryItemAssemblyDTO>(
      `${this.basePath}/api/inventory/ItemAssemblies/${itemAssembly.id}`,
      itemAssembly);
  }

  public deleteInventoryItemAssembly(id: number): Observable<any> {
    return this.httpClient.delete(
      `${this.basePath}/api/inventory/ItemAssemblies/${id}`);
  }

  public getInventoryItemAssemblyAttributes(includeInactive: boolean = false) {
    let queryParameters = new HttpParams();

    if (includeInactive !== undefined && includeInactive !== null) {
      queryParameters = queryParameters.set(
        "IncludeInactive",
        <any>includeInactive
      );
    }

    return this.httpClient.get<InventoryItemAssemblyAttributeDTO[]>(
      `${this.basePath}/api/inventory/ItemAssemblyAttributes`,
      {
        params: queryParameters
      }
    );
  }

  public deleteInventoryItemAssemblyComponent(id: number): Observable<any> {
    return this.httpClient.delete(
      `${this.basePath}/api/inventory/ItemAssemblyComponents/${id}`
    );
  }

  public postInventoryFromAssembly(
    itemAssembly: InventoryItemAssemblyDTO,
    warehouseId: number,
    roomId: number = 0,
    locationId: number = 0,
    itemCount: number = 0,
    workOrderId: number,
    printerId: number = 0
  ): Observable<InventoryItemAssemblyDTO> {
    let queryParameters = new HttpParams();

    if (warehouseId !== undefined && warehouseId !== null && warehouseId != 0) {
      queryParameters = queryParameters.set("warehouseId", <any>warehouseId);
    }

    if (roomId !== undefined && roomId !== null && roomId != 0) {
      queryParameters = queryParameters.set("roomId", <any>roomId);
    }

    if (locationId !== undefined && locationId !== null && locationId != 0) {
      queryParameters = queryParameters.set("locationId", <any>locationId);
    }

    if (itemCount !== undefined && itemCount !== null && itemCount != 0) {
      queryParameters = queryParameters.set("itemCount", <any>itemCount);
    }

    if (workOrderId !== undefined && workOrderId !== null && workOrderId != 0) {
      queryParameters = queryParameters.set("workOrderId", <any>workOrderId);
    }

    if (printerId !== undefined && printerId !== null && printerId != 0) {
      queryParameters = queryParameters.set("printerId", <any>printerId);
    }

    // to determine the Content-Type header
    const consumes: string[] = [];

    return this.httpClient.post<InventoryItemAssemblyDTO>(
      `${this.basePath}/api/inventory/ItemAssemblies/InventoryFromAssembly`,
      itemAssembly,

      {
        params: queryParameters
      }
    );
  }

  public bulkMoveHandlingUnitMasters(
    request: HandlingUnitBulkMove
  ): Observable<any> {

    return this.httpClient.put(
      `${this.basePath}/api/inventory/bulkmove/`,
      request);
  }

  public bulkDeleteHandlingUnitMasters(
    request: HandlingUnitBulkDelete
  ): Observable<any> {

    return this.httpClient.put(
      `${this.basePath}/api/inventory/bulkdelete/`,
      request);
  }

  public getInventoryPackageTypes(includeInactive: boolean = false) {
    let queryParameters = new HttpParams();

    if (includeInactive !== undefined && includeInactive !== null) {
      queryParameters = queryParameters.set(
        "IncludeInactive",
        <any>includeInactive
      );
    }

    return this.httpClient.get<InventoryPackageTypeDTO[]>(
      `${this.basePath}/api/inventory/inventoryPackageTypes`,
      {
        params: queryParameters,
      }
    );
  }

  public getInventoryPackageType(
    itemId: number
  ): Observable<InventoryPackageTypeDTO> {

    return this.httpClient.get<InventoryPackageTypeDTO>(
      `${this.basePath}/api/inventory/inventoryPackageTypes/${itemId}`);
  }

  public postInventoryPackageType(
    item: InventoryPackageTypeDTO
  ): Observable<InventoryPackageTypeDTO> {

    return this.httpClient.post<InventoryItemDTO>(
      `${this.basePath}/api/inventory/inventoryPackageTypes`,
      item,
    );
  }

  public putInventoryPackageType(
    item: InventoryPackageTypeDTO
  ): Observable<InventoryPackageTypeDTO> {

    return this.httpClient.put<InventoryPackageTypeDTO>(
      `${this.basePath}/api/inventory/inventoryPackageTypes/${item.id}`,
      item
    );
  }

  public deleteInventoryPackageType(id: number): Observable<any> {
    return this.httpClient.delete(
      `${this.basePath}/api/inventory/inventoryPackageTypes/${id}`
    );
  }

  public deleteInventoryHUD(id: number): Observable<any> {
    return this.httpClient.delete(
      `${this.basePath}/api/inventory/HandlingUnitDetail/${id}`);
  }

  public reactivateInventoryHUD(id: number, reasonCodeId: number = 0): Observable<any> {
    let queryParameters = new HttpParams();

    if (reasonCodeId)
      queryParameters = queryParameters.set("reasonCodeId", reasonCodeId.toString());

    return this.httpClient.put(
      `${this.basePath}/api/inventory/HandlingUnitDetail/${id}/reactivate`,
      null,
      {
        params: queryParameters
      }
    );
  }

  public putInventoryHUD(
    hud: HandlingUnitDetailDTO,
    isERPSave?: boolean,
    actionType?: ActionTypeForInvetory
  ): Observable<HandlingUnitDetailDTO> {

    let queryParameters = new HttpParams();
    if (isERPSave)
      queryParameters = queryParameters.set(
        "saveERPFlag",
        isERPSave.toString()
      );

    if (!Utils.IsStringNullOrEmpty(actionType)) {
      queryParameters = queryParameters.set(
        "actionType",
        actionType.toString()
      );
    }
    return this.httpClient.put<HandlingUnitDetailDTO>(
      `${this.basePath}/api/inventory/HandlingUnitDetail/${hud.id}`,
      hud,
      {
        params: queryParameters
      }
    );
  }

  public getReasonCodes(includeInactive: boolean = false) {
    let queryParameters = new HttpParams();

    if (includeInactive !== undefined && includeInactive !== null) {
      queryParameters = queryParameters.set(
        "IncludeInactive",
        <any>includeInactive
      );
    }


    // return this.httpClient.get<ReasonCode[]>(`${this.basePath}/api/inventory/reasonCodes`, //This doesn't even exist???
    return this.httpClient.get<ReasonCode[]>(
      `${this.basePath}/api/transactionReasonCodes`,

      {
        params: queryParameters
      }
    );
  }

  public refreshLocalLots() {
    // this.getInventoryLots().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(lots => {
    //   this.allLots = lots;
    // }, err => this.errorService.handleError(err));
    this.getInventoryLotsForFilters().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({next:
      (lots) => {
        this.allLotsForFilters = lots;
      },
      error:err => this.errorService.handleError(err)});
      // (error:err) => {this.errorService.handleError(err)}
  }

  public getLotMassBalance(lotId: number): Observable<any[]> {
    return this.httpClient.get<any[]>(
      `${this.basePath}/api/inventory/lots/massbalance/${lotId}`);
  }

  public getPackoutReport(year: string): Observable<any[]> {
    return this.httpClient.get<any[]>(
      `${this.basePath}/api/inventory/Packout/summary?year=${year}`);
  }

  public updateMassBalanceReason(record: any): Observable<any> {
    return this.httpClient.put(
      `${this.basePath}/api/inventory/lots/update-mass-balance-record`,
      record);
  }

  public getAllIntegrationId() {
    return this.httpClient.get<string[]>(
      `${this.basePath}/api/inventory/HandlingUnitMaster/getAllIntegrationId`);
  }

  public getAllByIntegrationId(integrationId: string) {
    return this.httpClient.get(
      `${this.basePath}/api/inventory/HandlingUnitMaster/getAllInventoryByIntegrationId?integrationId=${integrationId}`);
  }

  //Update Hot Room fields
  public updateHotRoomBulk(items: HandlingUnitMasterDTO[]): Observable<HandlingUnitMasterDTO[]> {
    return this.httpClient.put<HandlingUnitMasterDTO[]>(
      `${this.basePath}/api/inventory/HandlingUnitMaster/updateHotRoomBulk`,
      items
    );
  }

  //Get all Barcodes list by it's ids
  public getAllHUMbyId(ids: number[]) {
    return this.httpClient.post(
      `${this.basePath}/api/inventory/HandlingUnitMaster/getAllHUMbyId`,
      ids
    );
  }

  //Get only Lots without hum and other references
  public getOnlyLots() {
    let queryParameters = new HttpParams();

    return this.httpClient.get<InventoryLotDTO[]>(
      `${this.basePath}/api/inventory/lots/getonlylots`,
      {
        params: queryParameters
      }
    );
  }

  public moveToOrderHUM(request: {
    handlingUnitDetailIds: number[],
    quantities: number[],
    orderNumber: string,
    reasonCodeId?: number,
    details?: string
  }): Observable<any> {
    return this.httpClient.put(
      `${this.basePath}/api/inventory/bulkMove/moveToOrderHUM`,
      request
    );
  }

}
